import { IScreenCoords } from '@newroom-connect/library/interfaces';

export interface ITransform {
  element: HTMLElement;
  style: string;
  computedStyle: CSSStyleDeclaration;
  width: number;
  height: number;
  useBackfacing: boolean;
  topLeft: IScreenCoords;
  topRight: IScreenCoords;
  bottomLeft: IScreenCoords;
  bottomRight: IScreenCoords;
}

/**
 * Enhanced matrix transformation helper based on the old working TransformService.
 * Provides sophisticated matrix calculations with proper Gaussian elimination.
 */
export class MatrixHelper {
  /**
   * Create identity matrix3d string.
   *
   * @returns Identity matrix3d CSS string.
   */
  public static createIdentityMatrix(): string {
    return 'matrix3d(1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1)';
  }

  /**
   * Check if matrix is identity matrix.
   *
   * @param matrix - Matrix3d CSS string.
   * @returns Whether matrix is identity.
   */
  public static isIdentityMatrix(matrix: string): boolean {
    if (!matrix) {
      return true;
    }

    const values = this.parseMatrixValues(matrix);

    if (!values || values.length !== 16) {
      return false;
    }

    for (let i = 0; i < values.length; i++) {
      const expectedValue = (i === 0 || i === 5 || i === 10 || i === 15) ? 1 : 0;

      if (values[i] !== expectedValue) {
        return false;
      }
    }

    return true;
  }

  /**
   * Parse matrix3d values from CSS string.
   *
   * @param matrix - Matrix3d CSS string.
   * @returns Array of numeric values or null if invalid.
   */
  public static parseMatrixValues(matrix: string): number[] | null {
    if (!matrix || !matrix.includes('matrix3d')) {
      return null;
    }

    const valueString = matrix.slice(9, matrix.length - 1);
    const stringValues = valueString.split(',');

    if (stringValues.length !== 16) {
      return null;
    }

    const numericValues: number[] = [];

    for (const value of stringValues) {
      const num = Number(value.trim());

      if (isNaN(num)) {
        return null;
      }
      numericValues.push(num);
    }

    return numericValues;
  }

  /**
   * Create matrix3d from transformation coefficients.
   *
   * @param coefficients - Array of 8 transformation coefficients.
   * @returns Matrix3d CSS string.
   */
  public static createMatrixFromCoefficients(coefficients: number[]): string {
    if (!coefficients || coefficients.length !== 8) {
      return this.createIdentityMatrix();
    }

    return `matrix3d(${coefficients[0].toFixed(9)},${coefficients[3].toFixed(9)},0,${coefficients[6].toFixed(9)},${coefficients[1].toFixed(9)},${coefficients[4].toFixed(9)},0,${coefficients[7].toFixed(9)},0,0,1,0,${coefficients[2].toFixed(9)},${coefficients[5].toFixed(9)},0,1)`;
  }

  /**
   * Calculate transformation matrix using proper perspective transform calculations.
   * Based on the old working TransformService implementation.
   *
   * @param transformObject - Transform object with corner coordinates and dimensions.
   * @returns Updated transform object with calculated matrix3d style.
   */
  public static transform(transformObject: ITransform): ITransform {
    const matrix = [
      [0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 1, 0, 0, 0, 0, 0],
      [0, 0, 0, 0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0, 1, 0, 0],
      [0, 0, 0, 0, 0, 1, 0, 0]
    ];

    const coefficients = [0, 0, 0, 0, 0, 0, 0, 0];
    const offset = this.setOffset(transformObject);

    if (offset) {
      const offsetX = offset.x;
      const offsetY = offset.y;

      const corners = [transformObject.topLeft, transformObject.topRight, transformObject.bottomLeft, transformObject.bottomRight];
      const transformationCoefficients = [0, 1, 2, 3, 4, 5, 6, 7];
      let kmax: number;
      let sum: number;
      let row: number[];
      const col = [];
      let i: number;
      let j: number;
      let k: number;
      let tmp: number;

      for (let index = 0; index < 4; index++) {
        matrix[index][0] = matrix[index + 4][3] = index % 2 ? transformObject.width + offsetX : offsetX;
        matrix[index][1] = matrix[index + 4][4] = (index > 1 ? transformObject.height + offsetY : offsetY);
        matrix[index][6] = (index % 2 ? -offsetX - transformObject.width : -offsetX) * (corners[index].x + offsetX);
        matrix[index][7] = (index > 1 ? -offsetY - transformObject.height : -offsetY) * (corners[index].x + offsetX);
        matrix[index + 4][6] = (index % 2 ? -offsetX - transformObject.width : -offsetX) * (corners[index].y + offsetY);
        matrix[index + 4][7] = (index > 1 ? -offsetY - transformObject.height : -offsetY) * (corners[index].y + offsetY);
        coefficients[index] = (corners[index].x + offsetX);
        coefficients[index + 4] = (corners[index].y + offsetY);
        matrix[index][2] = matrix[index + 4][5] = 1;
        matrix[index][3] = matrix[index][4] = matrix[index][5] = matrix[index + 4][0] = matrix[index + 4][1] = matrix[index + 4][2] = 0;
      }

      // Proper Gaussian elimination with LU decomposition
      for (j = 0; j < 8; j++) {
        for (i = 0; i < 8; i++) {
          col[i] = matrix[i][j];
        }

        for (i = 0; i < 8; i++) {
          row = matrix[i];
          kmax = i < j ? i : j;
          sum = 0.0;

          for (k = 0; k < kmax; k++) {
            sum += row[k] * col[k];
          }

          row[j] = col[i] -= sum;
        }

        let p = j;

        for (i = j + 1; i < 8; i++) {
          if (Math.abs(col[i]) > Math.abs(col[p])) {
            p = i;
          }
        }

        if (p !== j) {
          for (k = 0; k < 8; k++) {
            tmp = matrix[p][k];
            matrix[p][k] = matrix[j][k];
            matrix[j][k] = tmp;
          }
          tmp = transformationCoefficients[p];
          transformationCoefficients[p] = transformationCoefficients[j];
          transformationCoefficients[j] = tmp;
        }

        if (matrix[j][j] !== 0.0) {
          for (i = j + 1; i < 8; i++) {
            matrix[i][j] /= matrix[j][j];
          }
        }
      }

      for (i = 0; i < 8; i++) {
        transformationCoefficients[i] = coefficients[transformationCoefficients[i]];
      }

      for (k = 0; k < 8; k++) {
        for (i = k + 1; i < 8; i++) {
          transformationCoefficients[i] -= transformationCoefficients[k] * matrix[i][k];
        }
      }

      for (k = 7; k > -1; k--) {
        transformationCoefficients[k] /= matrix[k][k];

        for (i = 0; i < k; i++) {
          transformationCoefficients[i] -= transformationCoefficients[k] * matrix[i][k];
        }
      }

      const style =
        `matrix3d(${
          transformationCoefficients[0].toFixed(9)
        },${
          transformationCoefficients[3].toFixed(9)
        }, 0,${
          transformationCoefficients[6].toFixed(9)
        },${
          transformationCoefficients[1].toFixed(9)
        },${
          transformationCoefficients[4].toFixed(9)
        }, 0,${
          transformationCoefficients[7].toFixed(9)
        },0, 0, 1, 0,${
          transformationCoefficients[2].toFixed(9)
        },${
          transformationCoefficients[5].toFixed(9)
        }, 0, 1)`;

      transformObject.style = style;
    }

    return transformObject;
  }

  /**
   * Calculate transform-origin offset from CSS computed style.
   *
   * @param transform - Transform object with computed style.
   * @returns Offset coordinates or null if not found.
   */
  private static setOffset(transform: ITransform): IScreenCoords | null {
    const offset = transform.computedStyle.getPropertyValue('transform-origin');

    if (offset.indexOf('px') > -1) {
      const offsetParts = offset.split('px');

      return { x: -parseFloat(offsetParts[0]), y: -parseFloat(offsetParts[1]) };
    } else if (offset.indexOf('%') > -1) {
      const offsetParts = offset.split('%');

      return {
        x: (-parseFloat(offsetParts[0]) * transform.width / 100),
        y: (-parseFloat(offsetParts[1]) * transform.height / 100)
      };
    }

    return null;
  }

  /**
   * Check if transformation has geometric errors (invalid quadrilateral).
   *
   * @param transformObject - Transform object to validate.
   * @returns True if transformation has errors.
   */
  public static hasTransformError(transformObject: ITransform): boolean {
    const det1 = this.getDeterminant(
      transformObject.topLeft,
      transformObject.topRight,
      transformObject.bottomRight
    );
    const det2 = this.getDeterminant(
      transformObject.bottomRight,
      transformObject.bottomLeft,
      transformObject.topLeft
    );

    if (det1 <= 0 || det2 <= 0) {
      return true;
    }

    const det3 = this.getDeterminant(
      transformObject.topRight,
      transformObject.bottomRight,
      transformObject.bottomLeft
    );
    const det4 = this.getDeterminant(
      transformObject.bottomLeft,
      transformObject.topLeft,
      transformObject.topRight
    );

    if (det3 <= 0 || det4 <= 0) {
      return true;
    }

    return false;
  }

  /**
   * Calculate determinant for three points to check quadrilateral validity.
   *
   * @param p0 - First point.
   * @param p1 - Second point.
   * @param p2 - Third point.
   * @returns Determinant value.
   */
  private static getDeterminant(p0: IScreenCoords, p1: IScreenCoords, p2: IScreenCoords): number {
    return p0.x * p1.y + p1.x * p2.y + p2.x * p0.y - p0.y * p1.x - p1.y * p2.x - p2.y * p0.x;
  }
}
